import React, { useEffect, useState, useCallback } from "react";
import { Archive, Award, TrendingDown, Scale, Trophy, ClipboardList, Users, Flag,
  FileSpreadsheet, FileText, Search, ArchiveRestore, ChevronLeft, CalendarPlus, Shield } from "lucide-react";
import { toast } from "sonner";
import SummaryCard from "@/components/SummaryCard";
import PageHeader, { schoolYear } from "@/components/PageHeader";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function ArchiveCenter() {
  const [seasons, setSeasons] = useState([]);
  const [activeYear, setActiveYear] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [rollOpen, setRollOpen] = useState(false);
  const [phrase, setPhrase] = useState("");
  const [rolling, setRolling] = useState(false);
  const nextYear = activeYear != null ? activeYear + 1 : null;
  const rollPhrase = nextYear != null ? `START ${nextYear}-${nextYear + 1}` : "";

  const doRollover = async () => {
    setRolling(true);
    try {
      const { data } = await api.post("/admin/archive/rollover");
      toast.success(`Season advanced to ${data.to_year}-${data.to_year + 1}`, {
        description: `${data.merits_archived} merit + ${data.lettering_archived} lettering records archived · ${data.teams_rolled} teams & ${data.people_rolled} people rolled forward${data.graduated_deactivated ? ` · ${data.graduated_deactivated} graduated deactivated` : ""}`,
      });
      setRollOpen(false); setPhrase("");
      loadSeasons();
    } catch (e) { toast.error(e.response?.data?.detail || "Rollover failed"); }
    finally { setRolling(false); }
  };

  const loadSeasons = useCallback(async () => {
    const { data } = await api.get("/admin/archive/seasons");
    setSeasons(data.seasons);
    setActiveYear(data.active_year);
  }, []);
  useEffect(() => { loadSeasons(); }, [loadSeasons]);

  const openSeason = useCallback(async (year) => {
    setSelected(year); setDetail(null); setSummary(null); setQ("");
    const [d, s] = await Promise.all([
      api.get(`/admin/archive/seasons/${year}`),
      api.get(`/admin/reports/summary?year=${year}`),
    ]);
    setDetail(d.data); setSummary(s.data);
  }, []);

  const refreshSeason = useCallback(async () => {
    if (selected == null) return;
    const { data } = await api.get(`/admin/archive/seasons/${selected}`);
    setDetail(data);
    loadSeasons();
  }, [selected, loadSeasons]);

  const download = async (entity, format) => {
    const key = `${entity}-${format}`;
    setBusy(key);
    try {
      const url = `${BASE}/admin/exports/${entity}?format=${format}&detail=true&year=${selected}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${localStorage.getItem("rallypointgo_token")}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${entity}_${selected}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Export downloaded");
    } catch (e) { toast.error(e.message); }
    finally { setBusy(""); }
  };

  const doArchiveToggle = async () => {
    const t = archiveTarget; setArchiveTarget(null);
    try {
      await api.post(`/admin/teams/${t.id}/archive?archived=${!t.archived}`);
      toast.success(t.archived ? "Team unarchived" : "Team archived");
      refreshSeason();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  // ----- Season list view -----
  if (selected == null) {
    return (
      <div>
        <PageHeader
          title="Archive Center"
          subtitle="Browse, search and export every past season in one place"
          actions={
            <Button className="rounded-full bg-slate-900 hover:bg-slate-800" onClick={() => setRollOpen(true)} data-testid="start-new-season-btn" disabled={activeYear == null}>
              <CalendarPlus className="mr-2 h-4 w-4" /> Start New Season
            </Button>
          }
        />
        {seasons.length === 0 ? (
          <Card className="p-10 text-center text-slate-500 border-slate-200" data-testid="archive-empty">
            No seasons found yet.
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="season-grid">
            {seasons.map((s) => (
              <button
                key={s.year}
                onClick={() => openSeason(s.year)}
                data-testid={`season-card-${s.year}`}
                className="group text-left rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition hover:border-slate-900 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-slate-900"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-900 text-white transition group-hover:scale-105">
                    <Archive className="h-5 w-5" />
                  </span>
                  {s.is_active
                    ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid={`season-active-${s.year}`}>Active season</Badge>
                    : <Badge variant="secondary" className="bg-slate-100 text-slate-600">Past season</Badge>}
                </div>
                <h3 className="mt-4 text-2xl font-bold text-slate-900 font-heading tabular-nums">{schoolYear(s.year)}</h3>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <Stat label="Teams" value={s.teams} hint={!s.is_active ? "read-only history" : (s.archived_teams ? `${s.archived_teams} archived` : "all active")} />
                  <Stat label="Members" value={s.members} />
                  <Stat label="Merit cases" value={s.merit_cases} />
                  <Stat label="Lettering" value={s.lettering_submissions} />
                </div>
              </button>
            ))}
          </div>
        )}
        {nextYear != null && (
          <Dialog open={rollOpen} onOpenChange={(o) => { setRollOpen(o); if (!o) setPhrase(""); }}>
            <DialogContent className="max-h-[90dvh] overflow-y-auto" data-testid="rollover-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading">Start the {nextYear}–{nextYear + 1} season?</DialogTitle>
                <DialogDescription>This affects the whole app and cannot be undone in one click.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm text-slate-600">
                <ul className="list-disc space-y-1 pl-5">
                  <li>Archives the {schoolYear(activeYear)} merit &amp; lettering records (kept fully searchable &amp; exportable here)</li>
                  <li>Rolls every active team and its coaches &amp; members into the new year with a clean slate</li>
                  <li>Graduated members are automatically deactivated</li>
                </ul>
                <div className="space-y-1.5">
                  <Label className="text-xs">Type <span className="font-mono font-semibold text-slate-800">{rollPhrase}</span> to confirm</Label>
                  <Input value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder={rollPhrase} data-testid="rollover-phrase-input" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => setRollOpen(false)} data-testid="rollover-cancel">Cancel</Button>
                <Button className="rounded-full bg-slate-900 hover:bg-slate-800" disabled={phrase !== rollPhrase || rolling} onClick={doRollover} data-testid="rollover-confirm-btn">
                  {rolling ? "Starting…" : "Start new season"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>
    );
  }

  // ----- Season detail view -----
  const teams = (detail?.teams || []).filter((t) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [t.name, t.code, t.campus_name, t.district_name].filter(Boolean)
      .some((v) => v.toLowerCase().includes(s));
  });

  return (
    <div>
      <div className="mb-2">
        <Button variant="ghost" size="sm" className="rounded-full -ml-2 text-slate-600" onClick={() => setSelected(null)} data-testid="archive-back">
          <ChevronLeft className="mr-1 h-4 w-4" /> All seasons
        </Button>
      </div>
      <PageHeader
        title={`Season ${schoolYear(selected)}`}
        subtitle={detail?.is_active ? "Active season — records are live" : "Archived season — read-only history"}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard testId="arch-merit-cases" label="Merit Cases" value={summary?.merit_cases ?? "—"} icon={Award} accent="slate" />
        <SummaryCard testId="arch-total-merits" label="Total Merits" value={summary?.total_merits ?? "—"} icon={Award} accent="emerald" />
        <SummaryCard testId="arch-total-demerits" label="Total Demerits" value={summary?.total_demerits ?? "—"} icon={TrendingDown} accent="red" />
        <SummaryCard testId="arch-net-score" label="Net Score" value={summary?.net_score ?? "—"} icon={Scale} accent="sky" />
        <SummaryCard testId="arch-lettering-subs" label="Lettering Subs" value={summary?.lettering_submissions ?? "—"} icon={ClipboardList} accent="slate" />
        <SummaryCard testId="arch-lettering-approved" label="Approved" value={summary?.lettering_approved ?? "—"} icon={Trophy} accent="emerald" />
        <SummaryCard testId="arch-lettering-pending" label="Pending" value={summary?.lettering_pending ?? "—"} icon={ClipboardList} accent="amber" />
        <SummaryCard testId="arch-approved-points" label="Approved Points" value={summary?.approved_lettering_points ?? "—"} icon={Shield} accent="lettering" />
      </div>

      <Card className="mt-6 p-5 border-slate-200 shadow-sm" data-testid="season-exports">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">Export this season</span>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download("merits", "csv")} data-testid="arch-export-merits-csv"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Merits CSV</Button>
            <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download("merits", "pdf")} data-testid="arch-export-merits-pdf"><FileText className="mr-1.5 h-4 w-4" /> Merits PDF</Button>
            <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download("lettering", "csv")} data-testid="arch-export-lettering-csv"><FileSpreadsheet className="mr-1.5 h-4 w-4" /> Lettering CSV</Button>
            <Button size="sm" variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download("lettering", "pdf")} data-testid="arch-export-lettering-pdf"><FileText className="mr-1.5 h-4 w-4" /> Lettering PDF</Button>
          </div>
        </div>
      </Card>

      <div className="mt-6 mb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-slate-800 font-heading flex items-center gap-2"><Flag className="h-5 w-5 text-slate-400" /> Teams ({teams.length})</h3>
        <div className="relative w-64 max-w-full">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team, campus, district" className="pl-9 rounded-full" data-testid="season-team-search" />
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <Table data-testid="season-teams-table">
          <TableHeader>
            <TableRow>
              <TableHead>Team</TableHead>
              <TableHead>Campus</TableHead>
              <TableHead>District</TableHead>
              <TableHead className="text-center"><Users className="mx-auto h-4 w-4" /></TableHead>
              <TableHead className="text-center">Merits</TableHead>
              <TableHead className="text-center">Lettering</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {teams.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-10 text-center text-slate-400">No teams match.</TableCell></TableRow>
            ) : teams.map((t) => (
              <TableRow key={t.id} data-testid={`season-team-${t.id}`}>
                <TableCell className="font-medium text-slate-800">{t.name} <span className="text-slate-400">({t.code})</span></TableCell>
                <TableCell className="text-sm text-slate-600">{t.campus_name} <span className="text-slate-400">{t.campus_code ? `(${t.campus_code})` : ""}</span></TableCell>
                <TableCell className="text-sm text-slate-600">{t.district_name || "—"}</TableCell>
                <TableCell className="text-center tabular-nums">{t.members}</TableCell>
                <TableCell className="text-center tabular-nums">{t.merit_cases}</TableCell>
                <TableCell className="text-center tabular-nums">{t.lettering_submissions}</TableCell>
                <TableCell>
                  {!detail?.is_active
                    ? <Badge variant="secondary" className="bg-slate-100 text-slate-500">History</Badge>
                    : t.archived
                      ? <Badge variant="secondary" className="bg-amber-100 text-amber-700">Archived</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}
                </TableCell>
                <TableCell className="text-right">
                  {detail?.is_active ? (
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => setArchiveTarget(t)} data-testid={`season-team-archive-${t.id}`}>
                      {t.archived ? <><ArchiveRestore className="mr-1.5 h-4 w-4" /> Unarchive</> : <><Archive className="mr-1.5 h-4 w-4" /> Archive</>}
                    </Button>
                  ) : (
                    <span className="text-xs text-slate-400">Read-only</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ConfirmDialog
        open={!!archiveTarget}
        onOpenChange={(o) => !o && setArchiveTarget(null)}
        title={archiveTarget?.archived ? `Unarchive ${archiveTarget?.name}?` : `Archive ${archiveTarget?.name}?`}
        description={archiveTarget?.archived
          ? "This restores the team and its merit/lettering records to the active set."
          : "This archives the team and all of its merit & lettering records. They remain fully searchable and exportable."}
        confirmText={archiveTarget?.archived ? "Unarchive" : "Archive"}
        onConfirm={doArchiveToggle}
        testId="season-archive-confirm"
      />
    </div>
  );
}

const Stat = ({ label, value, hint }) => (
  <div className="rounded-lg bg-slate-50 px-3 py-2">
    <p className="text-lg font-bold text-slate-900 tabular-nums leading-tight">{value}</p>
    <p className="text-xs font-semibold text-slate-500">{label}</p>
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);
